export const ACTION_TYPES = [
  { value: 'send_email', label: 'Send Email' },
  { value: 'create_task', label: 'Create Task' },
  { value: 'update_contact', label: 'Update Contact' },
  { value: 'create_deal', label: 'Create Deal' },
  { value: 'update_deal', label: 'Update Deal' },
  { value: 'add_tag', label: 'Add Tag' },
  { value: 'remove_tag', label: 'Remove Tag' },
  { value: 'webhook', label: 'Webhook' },
  { value: 'move_deal_stage', label: 'Verplaats deal naar stage' },
  { value: 'assign_user', label: 'Toewijzen aan persoon' },
  { value: 'create_deal_task', label: 'Maak taak aan in deal' },
  { value: 'delay', label: 'Wachten (delay)' },
  // Add more action types as needed
];

// Soorten taken die in een deal aangemaakt kunnen worden (zie DealTasks.vue).
export const DEAL_TASK_TYPES = [
  { value: 'general', label: 'Algemeen' },
  { value: 'callback', label: 'Terugbellen' },
  { value: 'follow_up', label: 'Opvolgen' },
];

// Prioriteiten voor taken (zie DealTasks.vue).
export const TASK_PRIORITIES = [
  { value: 'low', label: 'Laag' },
  { value: 'medium', label: 'Middel' },
  { value: 'high', label: 'Hoog' },
];

// Hoe een deal aan een persoon wordt toegewezen.
// - 'specific': de gebruiker kiest één vaste persoon.
// - 'random':  de deal wordt willekeurig verdeeld over een pool van personen.
export const ASSIGNMENT_MODES = [
  {
    value: 'specific',
    label: 'Kiezen',
    description: 'Wijs elke nieuwe deal toe aan één vaste persoon.',
    icon: 'user',
  },
  {
    value: 'random',
    label: 'Willekeurig verdelen',
    description: 'Verdeel nieuwe deals willekeurig over de gekozen personen.',
    icon: 'users',
  },
];

// Allowed time-units for the `delay` action. The backend should accept the
// same enum values verbatim. Order is from smallest to largest.
export const DELAY_UNITS = [
  { value: 'minutes', label: 'Minuten', secondsPerUnit: 60 },
  { value: 'hours', label: 'Uren', secondsPerUnit: 60 * 60 },
  { value: 'days', label: 'Dagen', secondsPerUnit: 60 * 60 * 24 },
  { value: 'weeks', label: 'Weken', secondsPerUnit: 60 * 60 * 24 * 7 },
];

// Hard-cap to keep automations sane. If a user wants to wait longer than this
// they can chain two delay-nodes.
export const DELAY_MAX_VALUE_PER_UNIT = {
  minutes: 60 * 24 * 7, // 1 week in minutes
  hours: 24 * 30, // ~30 days
  days: 365, // 1 year
  weeks: 52, // 1 year
};

// Action-types that require a deal-trigger context. Shown greyed-out in the
// action picker when the automation is triggered by something else.
export const DEAL_ACTION_TYPES = ['move_deal_stage', 'assign_user'];
export const DEAL_TRIGGER_TYPES = ['deal_created', 'deal_status_change'];

// Triggers that can create a CRM task (deal home or project home).
export const TASK_CONTEXT_TRIGGER_TYPES = [
  'deal_created',
  'deal_status_change',
  'proposal_accepted',
  'proposal_rejected',
  'proposal_status_change',
  'project_status_change',
  'work_order_completed',
  'work_visit_confirmed',
  'work_visit_completed',
  'project_all_work_orders_completed',
];

// Template-variabelen die in tekstvelden van automation-stappen gebruikt kunnen
// worden (via {{variabele}}). Dit is de enige bron van waarheid; zowel de
// integratie-stappen (var-picker) als de deal-taak-config tonen deze lijst.
// Let op: `proposal.*` is niet beschikbaar bij de `deal_created`-trigger.
export const TEMPLATE_VARIABLES = [
  {
    group: 'Offerte',
    vars: [
      { key: 'proposal.total_amount', example: '3898' },
      { key: 'proposal.title', example: 'Offerte - Zipscreen' },
      { key: 'proposal.url', example: 'https://view.getsmartflow.nl/offerte/...' },
      { key: 'proposal.products', example: 'Zipscreen (2x), Rolluik' },
      { key: 'proposal.status', example: 'accepted' },
      { key: 'proposal.proposal_number', example: '47' },
      { key: 'proposal.currency', example: 'EUR' },
      { key: 'proposal.proposal_type', example: 'definitive' },
      { key: 'proposal.accepted_at', example: '2026-04-13T08:55:47.893Z' },
      { key: 'proposal.sent_at', example: '2026-04-13T08:55:47.893Z' },
      { key: 'proposal.valid_until', example: '2026-05-13T00:00:00+00:00' },
    ],
  },
  {
    group: 'Deal',
    vars: [
      { key: 'deal.id', example: 'a1b2c3d4-...' },
      { key: 'deal.value', example: '3898' },
      { key: 'deal.title', example: 'Prijsindicatie' },
      { key: 'deal.currency', example: 'EUR' },
      { key: 'deal.stage_name', example: 'Gewonnen' },
      { key: 'to_stage.name', example: 'Gewonnen' },
    ],
  },
  {
    group: 'Lead',
    vars: [
      { key: 'lead.name', example: 'Cas van Grunsven' },
      { key: 'lead.first_name', example: 'Cas' },
      { key: 'lead.last_name', example: 'van Grunsven' },
      { key: 'lead.email', example: 'cas@codigital.nl' },
      { key: 'lead.phone', example: '+31610806377' },
    ],
  },
  {
    group: 'Project',
    vars: [
      { key: 'project.id', example: 'a1b2c3d4-...' },
      { key: 'project.title', example: 'Installatie De Vries' },
      { key: 'project.status', example: 'completed' },
      { key: 'project.project_number', example: 'PRJ-001' },
    ],
  },
  {
    group: 'Werkbon',
    vars: [
      { key: 'work_order.id', example: 'a1b2c3d4-...' },
      { key: 'work_order.work_order_number', example: 'WO-001' },
      { key: 'work_order.work_order_type', example: 'installation' },
      { key: 'work_order.appointment_start', example: '2026-08-20T08:00:00Z' },
      { key: 'work_order.photo_count', example: '4' },
      { key: 'work_order.photo_urls', example: 'https://..., https://...' },
    ],
  },
  {
    group: 'Monteur',
    vars: [
      { key: 'installer.name', example: 'Jeroen Jansen en Henk de Wit' },
      { key: 'installer.first_name', example: 'Jeroen' },
      { key: 'installer.last_name', example: 'Jansen' },
      { key: 'installer.avatar_url', example: 'https://...' },
    ],
  },
  {
    group: 'Bezoek',
    vars: [
      { key: 'visit.crew', example: 'Jeroen Jansen, Henk de Wit, Erik Vos en Klaas Bakker' },
      { key: 'visit.crew_by_day', example: 'maandag 9 maart: Jeroen en Henk; dinsdag 10 maart: Erik en Klaas' },
      { key: 'visit.current_crew', example: 'Erik Vos en Klaas Bakker' },
      { key: 'visit.date_range', example: 'maandag 9 maart t/m donderdag 12 maart' },
      { key: 'visit.day_count', example: '4' },
      { key: 'visit.is_multi_day', example: 'true' },
      { key: 'visit.is_last_on_project', example: 'true' },
    ],
  },
];

export const TRIGGER_TYPES = [
  { value: 'form_submission', label: 'Form Submission' },
  { value: 'lead_status_change', label: 'Lead Status Change' },
  { value: 'deal_created', label: 'Deal aangemaakt' },
  { value: 'deal_status_change', label: 'Deal Status Change' },
  { value: 'proposal_accepted', label: 'Offerte geaccepteerd' },
  { value: 'proposal_rejected', label: 'Offerte afgewezen' },
  { value: 'proposal_status_change', label: 'Offerte status wijziging' },
  { value: 'project_status_change', label: 'Project status wijziging' },
  { value: 'project_fully_completed', label: 'Projectstatus: afgerond' },
  { value: 'work_order_completed', label: 'Werkbon afgerond' },
  { value: 'work_visit_confirmed', label: 'Bezoek bevestigd' },
  { value: 'work_visit_completed', label: 'Bezoek afgerond' },
  { value: 'project_all_work_orders_completed', label: 'Alle werkbonnen afgerond' },
  { value: 'scheduled', label: 'Scheduled' },
];

export const WORK_ORDER_TRIGGER_TYPES = [
  'work_order_completed',
  'work_visit_confirmed',
  'work_visit_completed',
  'project_all_work_orders_completed',
];

// Triggers die op een offerte-event vuren (zie webhook-integratie). Werken ook
// voor offertes zonder gekoppelde deal.
export const PROPOSAL_TRIGGER_TYPES = [
  'proposal_accepted',
  'proposal_rejected',
  'proposal_status_change',
];

// Optioneel filter op offerte-type voor de proposal-triggers. 'any' (of leeg)
// laat de automation voor elk offerte-type vuren.
export const PROPOSAL_TYPE_OPTIONS = [
  { value: 'any', label: 'Alle types' },
  { value: 'definitive', label: 'Definitief' },
  { value: 'proforma', label: 'Voorlopig' },
  { value: 'additional', label: 'Aanvullend' },
];

export const PROJECT_STATUS_OPTIONS = [
  { value: 'any', label: 'Elke status' },
  { value: 'draft', label: 'Concept' },
  { value: 'planning', label: 'Planning' },
  { value: 'active', label: 'Actief' },
  { value: 'completed', label: 'Afgerond' },
  { value: 'cancelled', label: 'Geannuleerd' },
];

export const PROPOSAL_STATUS_OPTIONS = [
  { value: 'any', label: 'Elke status' },
  { value: 'draft', label: 'Concept' },
  { value: 'sent', label: 'Verzonden' },
  { value: 'viewed', label: 'Bekeken' },
  { value: 'accepted', label: 'Geaccepteerd' },
  { value: 'rejected', label: 'Afgewezen' },
  { value: 'withdrawn', label: 'Ingetrokken' },
  { value: 'superseded', label: 'Vervangen' },
];

export const WORK_ORDER_TYPE_OPTIONS = [
  { value: 'any', label: 'Alle types' },
  { value: 'installation', label: 'Installatie' },
  { value: 'measurement', label: 'Inmeten' },
  { value: 'service', label: 'Service' },
];

/**
 * Map UI-only trigger aliases to the backend trigger_type + details.
 * `project_fully_completed` is a convenience preset for project_status_change
 * with toStatus=completed.
 */
export function normalizeTriggerForSave(triggerType, triggerDetails = {}) {
  if (triggerType === 'project_fully_completed') {
    return {
      triggerType: 'project_status_change',
      triggerDetails: {
        ...triggerDetails,
        fromStatus: triggerDetails.fromStatus || null,
        toStatus: 'completed',
      },
    };
  }
  return { triggerType, triggerDetails };
}

/**
 * Map a saved project_status_change with toStatus=completed (and no from) back
 * to the UI preset for clearer display.
 */
export function enrichTriggerForDisplay(triggerType, triggerDetails = {}) {
  if (
    triggerType === 'project_status_change' &&
    triggerDetails?.toStatus === 'completed' &&
    (!triggerDetails.fromStatus || triggerDetails.fromStatus === 'any')
  ) {
    return 'project_fully_completed';
  }
  return triggerType;
}